import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { listCertificates, getCertificatesSummary, createCertificate, updateCertificate, deleteCertificate, getImporters, getSuppliers, uploadCertificateFile } from '../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import {
  ShieldCheck, Plus, Search, Edit2, Trash2, AlertTriangle,
  CheckCircle, Clock, XCircle, Loader2, Calendar, Filter, FileText, Upload
} from 'lucide-react';

const STATUS_ICONS = {
  active: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-500/10 border-green-500/30' },
  expiring_soon: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-500/10 border-amber-500/30' },
  expired: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10 border-red-500/30' },
};

const EMPTY_FORM = {
  product: '', fraction: '', model: '', importer_id: '', supplier_id: '', certificate_number: '',
  issuing_authority: '', expiration_date: '', notes: '', cert_type: 'product_certificate',
};

export default function CertificatesPage() {
  const { t } = useI18n();
  const [certificates, setCertificates] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active: 0, expiring_soon: 0, expired: 0 });
  const [importers, setImporters] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterImporter, setFilterImporter] = useState('all');
  const [filterCertType, setFilterCertType] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});

  const handleFileUpload = async (certId, file) => {
    setUploading(prev => ({ ...prev, [certId]: true }));
    try {
      await uploadCertificateFile(certId, file);
      toast.success(t('certificates.fileUploaded'));
      loadData();
    } catch {
      toast.error(t('certificates.fileUploadError'));
    } finally {
      setUploading(prev => ({ ...prev, [certId]: false }));
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [certsRes, summaryRes, importersRes, suppliersRes] = await Promise.all([
        listCertificates({ search: search || undefined, status: filterStatus !== 'all' ? filterStatus : undefined, importer_id: filterImporter !== 'all' ? filterImporter : undefined, cert_type: filterCertType !== 'all' ? filterCertType : undefined }),
        getCertificatesSummary(),
        getImporters(),
        getSuppliers(),
      ]);
      setCertificates(certsRes.data);
      setSummary(summaryRes.data);
      setImporters(importersRes.data);
      setSuppliers(suppliersRes.data);
    } catch (err) {
      toast.error(t('certificates.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterImporter, filterCertType]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    if (!formData.product || !formData.expiration_date) {
      toast.error(t('common.required'));
      return;
    }
    setSaving(true);
    try {
      const imp = importers.find(i => i.id === formData.importer_id);
      const sup = suppliers.find(s => s.id === formData.supplier_id);
      const payload = { ...formData, importer_name: imp?.name || '', supplier_name: sup?.name || '' };
      
      if (editId) {
        await updateCertificate(editId, payload);
        toast.success(t('common.updatedOk'));
      } else {
        await createCertificate(payload);
        toast.success(t('common.createdOk'));
      }
      setDialogOpen(false);
      setEditId(null);
      setFormData(EMPTY_FORM);
      loadData();
    } catch (err) {
      toast.error(t('certificates.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (cert) => {
    setEditId(cert.id);
    setFormData({
      product: cert.product || '',
      fraction: cert.fraction || '',
      model: cert.model || '',
      importer_id: cert.importer_id || '',
      supplier_id: cert.supplier_id || '',
      certificate_number: cert.certificate_number || '',
      issuing_authority: cert.issuing_authority || '',
      expiration_date: cert.expiration_date?.substring(0, 10) || '',
      notes: cert.notes || '',
      cert_type: cert.cert_type || 'product_certificate',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (certId) => {
    if (!window.confirm(t('common.confirm') + '?')) return;
    try {
      await deleteCertificate(certId);
      toast.success(t('certificates.deletedOk'));
      loadData();
    } catch (err) {
      toast.error(t('certificates.errorDeleting'));
    }
  };

  const openNew = () => {
    setEditId(null);
    setFormData(EMPTY_FORM);
    setDialogOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="certificates-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading text-foreground flex items-center gap-2">
              <ShieldCheck className="text-primary" size={24} />
              {t('certificates.title')}
            </h1>
            <p className="text-muted-foreground mt-1">{t('certificates.subtitle')}</p>
          </div>
          <Button onClick={openNew} data-testid="new-certificate-btn">
            <Plus size={16} className="mr-1" />
            {t('certificates.addCertificate')}
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Total', value: summary.total, icon: ShieldCheck, color: 'text-blue-400' },
            { label: t('certificates.valid'), value: summary.active, icon: CheckCircle, color: 'text-green-400' },
            { label: t('certificates.expiringSoon') + ' (30d)', value: summary.expiring_soon, icon: AlertTriangle, color: 'text-amber-400' },
            { label: t('certificates.expired'), value: summary.expired, icon: XCircle, color: 'text-red-400' },
          ].map((card, i) => (
            <div key={i} className="bg-card border border-border rounded-lg p-4" data-testid={`cert-summary-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <card.icon size={16} className={card.color} />
                <span className="text-xs text-muted-foreground">{card.label}</span>
              </div>
              <p className="text-2xl font-heading">{card.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('certificates.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="cert-search"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36" data-testid="cert-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('certificates.allStatuses')}</SelectItem>
              <SelectItem value="active">{t('certificates.valid')}</SelectItem>
              <SelectItem value="expiring_soon">{t('certificates.expiringSoon')}</SelectItem>
              <SelectItem value="expired">{t('certificates.expired')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterImporter} onValueChange={setFilterImporter}>
            <SelectTrigger className="w-44" data-testid="cert-filter-importer">
              <Filter size={14} className="mr-1" />
              <SelectValue placeholder="Importadora" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {importers.map(imp => (
                <SelectItem key={imp.id} value={imp.id}>{imp.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterCertType} onValueChange={setFilterCertType}>
            <SelectTrigger className="w-44" data-testid="cert-filter-type">
              <FileText size={14} className="mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="product_certificate">{t('certificates.productCertificate')}</SelectItem>
              <SelectItem value="contrato_compraventa">{t('certificates.contract')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-primary" size={32} />
          </div>
        ) : certificates.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <ShieldCheck size={48} className="mx-auto mb-3 opacity-30" />
            <p>{t('certificates.noCertificatesFound')}</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left py-2.5 px-3 font-medium">{t('common.type')}</th>
                    <th className="text-left py-2.5 px-3 font-medium">{t('certificates.product')}</th>
                    <th className="text-left py-2.5 px-3 font-medium">{t('certificates.fraction')}</th>
                    <th className="text-left py-2.5 px-3 font-medium">{t('entities.importers')}</th>
                    <th className="text-left py-2.5 px-3 font-medium">{t('entities.suppliers')}</th>
                    <th className="text-left py-2.5 px-3 font-medium">{t('certificates.certificateNumber')}</th>
                    <th className="text-left py-2.5 px-3 font-medium">{t('certificates.expirationDate')}</th>
                    <th className="text-left py-2.5 px-3 font-medium">{t('common.status')}</th>
                    <th className="text-right py-2.5 px-3 font-medium">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {certificates.map((cert, i) => {
                    const statusCfg = STATUS_ICONS[cert.status] || STATUS_ICONS.active;
                    const StatusIcon = statusCfg.icon;
                    const statusLabels = { active: t('certificates.valid'), expiring_soon: t('certificates.expiringSoon'), expired: t('certificates.expired') };
                    return (
                      <tr key={cert.id} className="border-b border-border/50 hover:bg-muted/20" data-testid={`cert-row-${i}`}>
                        <td className="py-2.5 px-3">
                          <span className={`text-xs px-2 py-0.5 rounded ${cert.cert_type === 'contrato_compraventa' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/30' : 'bg-zinc-500/10 text-zinc-300 border border-zinc-500/30'}`}>
                            {cert.cert_type === 'contrato_compraventa' ? t('certificates.contract') : t('certificates.certificate')}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 font-medium">
                          <div className="flex items-center gap-1.5">
                            {cert.file_url && <FileText size={12} className="text-primary" />}
                            {cert.product}
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-muted-foreground">{cert.fraction || '-'}</td>
                        <td className="py-2.5 px-3">{cert.importer_name || '-'}</td>
                        <td className="py-2.5 px-3 text-muted-foreground">{cert.supplier_name || '-'}</td>
                        <td className="py-2.5 px-3 text-muted-foreground">{cert.certificate_number || '-'}</td>
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-1">
                            <Calendar size={12} className="text-muted-foreground" />
                            {formatDate(cert.expiration_date)}
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          <Badge variant="outline" className={`gap-1 ${statusCfg.bg}`}>
                            <StatusIcon size={10} className={statusCfg.color} />
                            {statusLabels[cert.status]}
                            {cert.days_until_expiration !== null && (
                              <span className="text-xs">
                                ({cert.days_until_expiration >= 0 ? `${cert.days_until_expiration}d` : `${Math.abs(cert.days_until_expiration)}d`})
                              </span>
                            )}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {cert.file_url ? (
                              <a href={cert.file_url} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                                data-testid={`cert-download-${i}`}
                              >
                                <FileText size={12} />
                                {cert.file_name || 'Archivo'}
                              </a>
                            ) : (
                              <label className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs cursor-pointer transition-colors ${uploading[cert.id] ? 'opacity-50 pointer-events-none' : 'bg-muted hover:bg-muted/80 text-muted-foreground'}`}>
                                <Upload size={12} />
                                {uploading[cert.id] ? '...' : 'Subir'}
                                <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                  data-testid={`cert-upload-${i}`}
                                  onChange={e => { if (e.target.files[0]) handleFileUpload(cert.id, e.target.files[0]); e.target.value = ''; }}
                                />
                              </label>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(cert)} data-testid={`cert-edit-${i}`}>
                              <Edit2 size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(cert.id)} data-testid={`cert-delete-${i}`}>
                              <Trash2 size={14} className="text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editId ? t('certificates.editCertificate') : t('certificates.addCertificate')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t('common.type')}</Label>
                <Select
                  value={formData.cert_type}
                  onValueChange={v => setFormData(p => ({ ...p, cert_type: v }))}
                >
                  <SelectTrigger data-testid="cert-form-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product_certificate">{t('certificates.productCertificate')}</SelectItem>
                    <SelectItem value="contrato_compraventa">{t('certificates.contract')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{formData.cert_type === 'contrato_compraventa' ? t('certificates.contractName') : t('certificates.product')} *</Label>
                <Input
                  value={formData.product}
                  onChange={e => setFormData(p => ({ ...p, product: e.target.value }))}
                  placeholder={formData.cert_type === 'contrato_compraventa' ? 'Contrato de Compraventa - CPCI / Isigo' : t('certificates.product')}
                  data-testid="cert-form-product"
                />
              </div>
              {formData.cert_type === 'product_certificate' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t('certificates.fraction')}</Label>
                    <Input
                      value={formData.fraction}
                      onChange={e => setFormData(p => ({ ...p, fraction: e.target.value }))}
                      placeholder="8471.30.01"
                      data-testid="cert-form-fraction"
                    />
                  </div>
                  <div>
                    <Label>{t('certificates.model')}</Label>
                    <Input
                      value={formData.model}
                      onChange={e => setFormData(p => ({ ...p, model: e.target.value }))}
                      data-testid="cert-form-model"
                    />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('entities.importers')}</Label>
                  <Select
                    value={formData.importer_id || '_none'}
                    onValueChange={v => setFormData(p => ({ ...p, importer_id: v === '_none' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="cert-form-importer">
                      <SelectValue placeholder={t('common.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">-</SelectItem>
                      {importers.map(imp => (
                        <SelectItem key={imp.id} value={imp.id}>{imp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('entities.suppliers')}</Label>
                  <Select
                    value={formData.supplier_id || '_none'}
                    onValueChange={v => setFormData(p => ({ ...p, supplier_id: v === '_none' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="cert-form-supplier">
                      <SelectValue placeholder={t('common.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">-</SelectItem>
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('certificates.certificateNumber')}</Label>
                  <Input
                    value={formData.certificate_number}
                    onChange={e => setFormData(p => ({ ...p, certificate_number: e.target.value }))}
                    data-testid="cert-form-number"
                  />
                </div>
                <div>
                  <Label>{t('certificates.expirationDate')} *</Label>
                  <Input
                    type="date"
                    value={formData.expiration_date}
                    onChange={e => setFormData(p => ({ ...p, expiration_date: e.target.value }))}
                    data-testid="cert-form-expiration"
                  />
                </div>
              </div>
              {formData.cert_type === 'product_certificate' && (
                <div>
                  <Label>{t('certificates.issuingAuthority')}</Label>
                  <Input
                    value={formData.issuing_authority}
                    onChange={e => setFormData(p => ({ ...p, issuing_authority: e.target.value }))}
                    placeholder="SE, COFEPRIS"
                    data-testid="cert-form-authority"
                  />
                </div>
              )}
              <div>
                <Label>{t('common.notes')}</Label>
                <Textarea
                  value={formData.notes}
                  onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                  rows={2}
                  data-testid="cert-form-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="cert-form-save">
                {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <CheckCircle size={14} className="mr-1" />}
                {editId ? t('common.save') : t('common.add')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
